import React, { useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  TickerCellRenderer,
  OptionTypeCellRenderer,
  UnixTimestampCellRenderer,
  PremiumCellRenderer,
  SpreadExecutionCellRenderer,
  SigScoreCellRenderer,
  TradeTypeCellRenderer,
  TradeSizeCellRenderer,
  TickerSentimentCellRenderer,
} from './VirtualizedFlowGridCellRendering';


// Default columns from your original implementation
const defaultColumns = [
  { field: 'ut', headerName: 'Time', width: 85, sortable: true, renderCell: (params) => <UnixTimestampCellRenderer data={params.row} /> },
  { field: 'p', headerName: 'Premium', width: 100, sortable: true, renderCell: (params) => <PremiumCellRenderer data={params.row} /> },
  { field: 'tsy', headerName: 'Ticker', width: 110, sortable: true, renderCell: (params) => <TickerCellRenderer data={params.row} /> },
  { field: 'sp', headerName: 'Spot', width: 90, sortable: true, valueGetter: (params) => params.row.sp ?? '-' },
  { field: 'stp', headerName: 'Strike', width: 90, sortable: true, valueGetter: (params) => params.row.stp ?? '-' },
  { field: 'ot', headerName: 'PC', width: 80, sortable: true, renderCell: (params) => <OptionTypeCellRenderer data={params.row} /> },
  { field: 'ed', headerName: 'Exp.', width: 120, sortable: true, valueGetter: (params) => params.row.ed ?? '-' },
  { field: 'se', headerName: 'X', width: 80, sortable: true, renderCell: (params) => <SpreadExecutionCellRenderer data={params.row} /> },
  { field: 'tt', headerName: 'Type', width: 80, sortable: true, renderCell: (params) => <TradeTypeCellRenderer data={params.row} /> },
  { field: 'tp', headerName: 'Price', width: 80, sortable: true, valueGetter: (params) => params.row.tp ?? '-' },
  { field: 'ts', headerName: 'Size', width: 80, sortable: true, renderCell: (params) => <TradeSizeCellRenderer data={params.row} /> },
  { field: 'cv', headerName: 'Vol.', width: 90, sortable: true, valueGetter: (params) => params.row.cv ?? '-' },
  { field: 'sc', headerName: 'Sig. Score', width: 90, sortable: true, renderCell: (params) => <SigScoreCellRenderer data={params.row} /> },
  { field: 'oi', headerName: 'OI', width: 80, sortable: true, valueGetter: (params) => params.row.oi ?? '-' },
  { field: 'iv', headerName: 'IV', width: 80, sortable: true, valueGetter: (params) => params.row.iv ?? '-' },
  { field: 'op', headerName: 'OTM%', width: 80, sortable: true, valueGetter: (params) => params.row.otm ?? '-' },
  {field: 'tks', headerName: 'Sentiment', width: 90, sortable: true, valueGetter: (params) => <TickerSentimentCellRenderer data={params.row} />},
];

// Default data from your original implementation
const defaultData = [
  {
    id: '1',
    sym: 'O:GOOGL250425C00147000',
    x: 6,
    tp: 18.08,
    ts: 500,
    p: 904000.0,
    dot: '04/25/25',
    ut: 1745845251,
    ot: 'C',
    tt: 'M',
    sp: 164.95,
    sc: 0.53,
    se: 'A',
    cb: 17.9,
    ca: 18.2,
    cs: 18.05,
    iv: 'N/A',
    oi: 1207,
    cv: 2404,
    lp: null,
    tg: ['c', 'a', 'st', 'we', 'vo'],
    g: null,
    mtd: 34200122,
    eut: 1745643540,
    edf: 'Apr 24, Post',
    ic: false,
    ios: false,
    xd: 0,
    s: '8',
    tdn: 'ucMo8HcR-',
  },
  {
    id: '2',
    sym: 'O:GOOGL250425C00147000',
    x: 6,
    tp: 18.08,
    ts: 500,
    p: 904000.0,
    dot: '04/25/25',
    ut: 1745845251,
    ot: 'C',
    tt: 'M',
    sp: 164.95,
    sc: 0.53,
    se: 'A',
    cb: 17.9,
    ca: 18.2,
    cs: 18.05,
    iv: 'N/A',
    oi: 1207,
    cv: 2404,
    lp: null,
    tg: ['c', 'a', 'st', 'we', 'vo'],
    g: null,
    mtd: 34200122,
    eut: 1745643540,
    edf: 'Apr 24, Post',
    ic: false,
    ios: false,
    xd: 0,
    s: '8',
    tdn: 'ucMo8HcR-',
  },
];

const VirtualizedFlowGrid = ({ data = [], rowHeight = 40, headerHeight = 35 }) => {
  // Process data to ensure unique IDs for MUI X Data Grid
  const rows = useMemo(() => {
    const inputData = data.length ? data : defaultData;
    return inputData.map((row, index) => ({
      ...row,
      id: row.id || `row-${index}`, // Ensure each row has a unique id
    }));
  }, [data]);

  // Manage column order for drag-and-drop simulation (Community version)
  const [colOrder, setColOrder] = useState(defaultColumns);

  // Handle column reordering (simulated drag-and-drop for Community version)
  const handleColumnOrderChange = (field, direction) => {
    setColOrder((prev) => {
      const index = prev.findIndex((col) => col.field === field);
      const newIndex = direction === 'left' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const newOrder = [...prev];
      const [moved] = newOrder.splice(index, 1);
      newOrder.splice(newIndex, 0, moved);
      return newOrder;
    });
  };

  // Custom sort model for MUI X Data Grid
  const [sortModel, setSortModel] = useState([
    { field: 'ut', sort: 'desc' }, // Default sort
  ]);

  // Handle sort changes
  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        borderRadius: '8px',      // rounded corners
        backgroundColor: 'black',
        overflow: 'hidden',       // clip child so corners stay rounded
      }}
    >
      {/* now allow horizontal scrolling at the wrapper level */}
      <div
        style={{
          width: '100%',
          height: '100%',
          overflowX: 'auto',
        }}
      >
        <DataGrid
          style={{
            width: '100%',
            maxWidth: '100%',  // never exceed parent
            height: '100%',
          }}
          rows={rows}
          columns={colOrder}
          rowHeight={rowHeight}
          headerHeight={headerHeight}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          disableSelectionOnClick
          hideFooter                // show all rows on one page
          sx={{
            /* no grid borders */
            border: 'none',
            '.MuiDataGrid-main': {
              border: 'none',
            },
            /* cell styling */
            '& .MuiDataGrid-cell': {
              all: 'unset',       // remove any default formatting
              color: 'white',
              fontWeight: 450,
              fontSize: '16px',
              padding: '4px 8px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              borderBottom: 'none',
            },
            /* header styling */
            '& .MuiDataGrid-columnHeaders': {
              all: 'unset',       // remove defaults
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'rgba(156, 163, 175, 1)',
              fontSize: '14px',
              fontWeight: 600,
              padding: '4px 8px',
              cursor: 'pointer',
              userSelect: 'none',
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'rgba(156, 163, 175, 1)',
            },
            /* let outer wrapper handle horizontal scroll */
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'visible',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 'none',
            },
          }}
          components={{
            ColumnHeaderMenu: ({ column }) => (
              <div style={{ display: 'flex', gap: '8px', padding: '8px' }}>
                <button
                  onClick={() => handleColumnOrderChange(column.field, 'left')}
                  disabled={
                    colOrder.findIndex(col => col.field === column.field) === 0
                  }
                  style={{ padding: '4px 8px', cursor: 'pointer' }}
                >
                  Move Left
                </button>
                <button
                  onClick={() => handleColumnOrderChange(column.field, 'right')}
                  disabled={
                    colOrder.findIndex(col => col.field === column.field) ===
                    colOrder.length - 1
                  }
                  style={{ padding: '4px 8px', cursor: 'pointer' }}
                >
                  Move Right
                </button>
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
  
  


};

export default VirtualizedFlowGrid;
